import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  mappingApi,
  connectorApi,
  type MappingItem,
  type ConnectorItem,
  type FieldMapping,
  type ValidationRule,
  TARGET_ENTITIES,
} from '../api/integrationApi'

export default function MappingEditPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [mapping, setMapping] = useState<MappingItem | null>(null)
  const [connectors, setConnectors] = useState<ConnectorItem[]>([])

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [sourceSystem, setSourceSystem] = useState('')
  const [targetEntity, setTargetEntity] = useState('')
  const [connectorId, setConnectorId] = useState<number | null>(null)
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([])
  const [validationRules, setValidationRules] = useState<ValidationRule[]>([])
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    if (!id) return
    const load = async () => {
      setLoading(true)
      try {
        const [data, connList] = await Promise.all([
          mappingApi.getById(Number(id)),
          connectorApi.list({ page: 1, pageSize: 100 }),
        ])
        setMapping(data)
        setConnectors(connList.items)
        setName(data.name)
        setDescription(data.description || '')
        setSourceSystem(data.sourceSystem)
        setTargetEntity(data.targetEntity)
        setConnectorId(data.connectorId)
        setIsActive(data.isActive)
        try { setFieldMappings(JSON.parse(data.mappings || '[]')) } catch { setFieldMappings([]) }
        try { setValidationRules(JSON.parse(data.validationRules || '[]')) } catch { setValidationRules([]) }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : t('integrations.errors.loadFailed'))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id, t])

  const handleSave = async () => {
    if (!name.trim() || !sourceSystem.trim() || !targetEntity) return
    setSaving(true)
    setError(null)
    try {
      await mappingApi.update(Number(id), {
        name: name.trim(),
        description: description.trim() || undefined,
        sourceSystem: sourceSystem.trim(),
        targetEntity,
        connectorId,
        mappings: JSON.stringify(fieldMappings),
        validationRules: validationRules.length > 0 ? JSON.stringify(validationRules) : undefined,
        isActive,
      })
      navigate('/system/integrations')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('integrations.errors.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const addFieldMapping = () => {
    setFieldMappings([...fieldMappings, { sourceField: '', targetField: '', isRequired: false }])
  }

  const updateFieldMapping = (index: number, field: Partial<FieldMapping>) => {
    const updated = [...fieldMappings]
    updated[index] = { ...updated[index], ...field }
    setFieldMappings(updated)
  }

  const removeFieldMapping = (index: number) => {
    setFieldMappings(fieldMappings.filter((_, i) => i !== index))
  }

  const addValidationRule = () => {
    setValidationRules([...validationRules, { field: '', rule: 'required' }])
  }

  const updateValidationRule = (index: number, field: Partial<ValidationRule>) => {
    const updated = [...validationRules]
    updated[index] = { ...updated[index], ...field }
    setValidationRules(updated)
  }

  const removeValidationRule = (index: number) => {
    setValidationRules(validationRules.filter((_, i) => i !== index))
  }

  const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
  const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-[1000px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('integrations.actions.edit')} — {mapping?.name}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('integrations.columns.mapping')} #{id}
          </p>
        </div>
        <button
          onClick={() => navigate('/system/integrations')}
          className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          {t('integrations.actions.cancel')}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* General */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('integrations.review.mapping')}</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>{t('integrations.fields.mappingName')} *</label>
            <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>{t('integrations.columns.sourceSystem')} *</label>
            <input className={inputCls} value={sourceSystem} onChange={(e) => setSourceSystem(e.target.value)} />
          </div>
        </div>

        <div>
          <label className={labelCls}>{t('integrations.fields.description')}</label>
          <textarea className={inputCls} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>{t('integrations.fields.targetEntity')} *</label>
            <select className={inputCls} value={targetEntity} onChange={(e) => setTargetEntity(e.target.value)}>
              <option value="">{t('integrations.fields.selectTargetEntity')}</option>
              {TARGET_ENTITIES.map((te) => (
                <option key={te} value={te}>{te}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>{t('integrations.columns.connector')}</label>
            <select className={inputCls} value={connectorId ?? ''} onChange={(e) => setConnectorId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">—</option>
              {connectors.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input type="checkbox" id="isActive" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="rounded border-gray-300" />
          <label htmlFor="isActive" className="text-sm text-gray-700 dark:text-gray-300">{t('integrations.columns.active')}</label>
        </div>
      </div>

      {/* Field Mappings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('integrations.builder.steps.fieldMapping')}</h2>
          <button onClick={addFieldMapping} className="px-3 py-1.5 text-xs bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
            + {t('integrations.builder.addMapping')}
          </button>
        </div>
        <div className="space-y-2">
          {fieldMappings.map((fm, i) => (
            <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <input
                className={`${inputCls} flex-1`}
                placeholder={t('integrations.builder.sourceField')}
                value={fm.sourceField}
                onChange={(e) => updateFieldMapping(i, { sourceField: e.target.value })}
              />
              <span className="text-gray-400">→</span>
              <input
                className={`${inputCls} flex-1`}
                placeholder={t('integrations.builder.targetField')}
                value={fm.targetField}
                onChange={(e) => updateFieldMapping(i, { targetField: e.target.value })}
              />
              <label className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                <input type="checkbox" checked={fm.isRequired || false} onChange={(e) => updateFieldMapping(i, { isRequired: e.target.checked })} />
                {t('integrations.builder.required')}
              </label>
              <button onClick={() => removeFieldMapping(i)} className="text-red-500 hover:text-red-700 text-sm px-1">✕</button>
            </div>
          ))}
          {fieldMappings.length === 0 && (
            <p className="text-sm text-gray-400">{t('integrations.builder.noValidationRules')}</p>
          )}
        </div>
      </div>

      {/* Validation Rules */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('integrations.builder.steps.validationRules')}</h2>
          <button onClick={addValidationRule} className="px-3 py-1.5 text-xs bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
            + {t('integrations.builder.addRule')}
          </button>
        </div>
        <div className="space-y-2">
          {validationRules.map((vr, i) => (
            <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <input
                className={`${inputCls} flex-1`}
                placeholder={t('integrations.builder.selectField')}
                value={vr.field}
                onChange={(e) => updateValidationRule(i, { field: e.target.value })}
              />
              <select className={`${inputCls} w-40`} value={vr.rule} onChange={(e) => updateValidationRule(i, { rule: e.target.value as ValidationRule['rule'] })}>
                <option value="required">{t('integrations.validationTypes.required')}</option>
                <option value="email">{t('integrations.validationTypes.email')}</option>
                <option value="date">{t('integrations.validationTypes.date')}</option>
                <option value="number">{t('integrations.validationTypes.number')}</option>
                <option value="regex">{t('integrations.validationTypes.regex')}</option>
              </select>
              <button onClick={() => removeValidationRule(i)} className="text-red-500 hover:text-red-700 text-sm px-1">✕</button>
            </div>
          ))}
          {validationRules.length === 0 && (
            <p className="text-sm text-gray-400">{t('integrations.builder.noValidationRules')}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving || !name.trim() || !sourceSystem.trim() || !targetEntity}
          className="px-6 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {saving ? t('integrations.actions.saving') : t('integrations.actions.edit')}
        </button>
      </div>
    </div>
  )
}
